function numberService(configService) {

  // Character Minimun Length Value.
  const TRACKING_NO_MIN_LENGTH = 9;
  // Character Maxmimum Length Value.
  const TRACKING_NO_MAX_LENGTH = 27;
  // Allowed Alphanumeric Tracking Number Lengths.
  const alphanumericFormatsLength = [9, 12, 13, 14];
  // Allowed Hexadecimal Tracking Number Lengths.
  const hexadecimalFormatsLength = [11, 16, 21];
  // Concatenation of two arrays - Used to map against any invalid lengths.
  const validStringLength = alphanumericFormatsLength.concat(hexadecimalFormatsLength);
  // Length of Tracking Number.
  let trackingNumberLength;

  // A Hexadecimal map used for odd number increments and Hexadecimal map for Hex Check.
  const alphabet = {
    0: 0,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6,
    7: 7,
    8: 8,
    9: 9,
    A: 10,
    B: 11,
    C: 12,
    D: 13,
    E: 14,
    F: 15,
    a: 10,
    b: 11,
    c: 12,
    d: 13,
    e: 14,
    f: 15
  };

  const errorMap = {
    INVALID_EMPTY: 'INVALID_EMPTY',
    INVALID_LENGTH: 'INVALID_LENGTH',
    INVALID_MIN_LENGTH: 'INVALID_MIN_LENGTH',
    INVALID_MAX_LENGTH: 'INVALID_MAX_LENGTH',
    INVALID_ALPHANUM: 'INVALID_ALPHANUM',
    INVALID_CP: 'INVALID_CP',
    INVALID_UID: 'INVALID_UID',
    INVALID_NINE: 'INVALID_NINE',
    INVALID_TWELVE: 'INVALID_TWELVE',
    INVALID_THIRTEEN: 'INVALID_THIRTEEN',
    INVALID_THIRTEEN_PFW: 'INVALID_THIRTEEN_PFW',
    INVALID_FOURTEEN: 'INVALID_FOURTEEN'
  };

  /**
   * @param {String} trackingNumber
   * @returns {String}
   */
  function sanitize(trackingNumber) {

    if (trackingNumber) {

      return trackingNumber.replace(/[^0-9a-zA-Z]/g, '');

    }

    return trackingNumber;

  }

  /**
   * @description : Calculates a Checksum.
   * @param {String} trackingNumber
   * @returns {String}
   */
  function checkSumCalculation(number) {

    const numLength = number.length - 1;
    let sum = 0;
    let alternate = 0;
    let currentCharacter;
    let index;

    // A Hexadecimal map with pre-calculated results for even numbers in the increment value.
    // The values within the map are provided by RMG.
    const doubleAlphabet = {
      0: 0,
      1: 2,
      2: 4,
      3: 6,
      4: 8,
      5: 5,
      6: 6,
      7: 7,
      8: 1,
      9: 3,
      A: 5,
      B: 7,
      C: 9,
      D: 13,
      E: 14,
      F: 15,
      a: 5,
      b: 7,
      c: 9,
      d: 13,
      e: 14,
      f: 15
    };

    for (index = numLength; index >= 0; index -= 1) {

      currentCharacter = number.charAt(index);

      alternate += 1;

      if ((alternate % 2) !== 0) {

        currentCharacter = alphabet[currentCharacter];

      } else {

        currentCharacter = doubleAlphabet[currentCharacter];

      }

      sum += currentCharacter;

    }

    return (sum % 16);

  }

  /**
   * @description : Validates S10 UPU Standard Check.
   * @param {String} trackingNumber
   * @returns {Boolean}
   */
  function checkS10Calculation(trackingNumber) {

    const weightArr = [8, 6, 4, 2, 3, 5, 9, 7];
    const trackingNumberArr = trackingNumber.split('');
    let sum = 0;
    let i = 0;
    let modSum = 0;
    for (i = 2; i < 10; i++) {

      sum += (weightArr[i - 2] * trackingNumberArr[i]);

    }

    modSum = 11 - (sum % 11);

    if (modSum === 10) {

      modSum = 0;

    } else if (modSum === 11) {

      modSum = 5;

    }

    return (parseInt(trackingNumberArr[10], 10) === modSum);

  }

  /**
   * @description : Validates if a Hex Value is entered.
   * @param {String} trackingNumber
   * @returns {Boolean}
   */
  function isHexNumber(trackingNumber) {

    for (let i = 0; i < trackingNumber.length; i++) {

      if (isNaN(parseInt(trackingNumber.charAt(i), 16))) {

        return false;

      }

    }

    return true;

  }

  /**
   * @description : Filter the number provided.
   * @param {String} trackingNumber
   * @returns {String} trackingNumber
   */
  function filterNumber(trackingNumber) {

    return trackingNumber.replace(/\s/g, '');

  }

  /**
   * Checking of passed value is number or not.
   * @param {Number} value
   * @returns {Boolean} true or false
   */
  function isNumber(value) {

    return (typeof (value) === 'number');

  }

  /**
   * Checking of passed value is zero or not.
   * @param {Number} value
   * @returns {Boolean} true or false
   */
  function isValueZero(value) {

    return value === 0;

  }

  /**
   * Validates coordinates.
   * @param {Number} latitude
   * @param {Number} longitude
   * @returns {Boolean} true or false
   */
  function areValidCoordinates(latitude, longitude) {

    if (isNumber(latitude) && isNumber(longitude)) {

      return (!isValueZero(latitude) || !isValueZero(longitude));

    }
    return false;

  }

  return {

    errorMap: errorMap,

    checkSumCalculation: checkSumCalculation,

    sanitize: sanitize,

    isHexNumber: isHexNumber,

    filterNumber: filterNumber,

    validate: (trackingNumber) => {

      // A number can't be blank.
      if (!trackingNumber || trackingNumber.length === 0) {

        return errorMap.INVALID_EMPTY;

      }

      // The tracking number has to be Alpha-Numeric
      // and has to have a valid string length.
      if (/[^0-9a-zA-Z]$/g.test(trackingNumber)) {

        return errorMap.INVALID_ALPHANUM;

      }

      trackingNumberLength = trackingNumber.length;

      // Check if invalid String length
      if (
        validStringLength.indexOf(trackingNumberLength) === -1
        && trackingNumberLength > TRACKING_NO_MIN_LENGTH
        && trackingNumberLength < TRACKING_NO_MAX_LENGTH
      ) {

        return errorMap.INVALID_LENGTH;

      }

      // Check if the tracking number is < 9.
      if (trackingNumber.length < TRACKING_NO_MIN_LENGTH) {

        return errorMap.INVALID_MIN_LENGTH;

      }

      // Check if the tracking number is > 27.
      if (trackingNumber.length > TRACKING_NO_MAX_LENGTH) {

        return errorMap.INVALID_MAX_LENGTH;

      }

      // Validate UID numbers.
      if (hexadecimalFormatsLength.indexOf(trackingNumberLength) !== -1 && isHexNumber(trackingNumber)) {

        if (checkSumCalculation(trackingNumber) === 0) {

          return true;

        }

        return errorMap.INVALID_UID;

      }

      // This is always a Parcelforce number.
      if (trackingNumberLength === 9) {

        return 'checkPFW';

      }

      // This is always a Parcelforce number.
      if (trackingNumberLength === 12) {

        return 'checkPFW';

      }

      if (trackingNumberLength === 13) {

        // Check if the number is in CP format;
        if ((/(^(CP)[0-9]{9})(GB)/ig.test(trackingNumber))) {

          return errorMap.INVALID_CP;

        }

        if (!(/[A-Z]{2}[0-9]{9}[A-Z]{2}/ig.test(trackingNumber))) {

          return 'checkPFW';

        }

        if (checkS10Calculation(trackingNumber)) {

          // GB suffix and NOT the following prefixes:
          // CK , CP, CU, EA, EB, ED, EE, EH, EK, EU, EC.
          // NO GB suffix and NOT the following prefixes:
          // CA-CZ, EA-EZ, BA-BZ, PA, ND-NS.
          if (
            !(/((CK)|(CP)|(CU)|(E[A-E])|(EH)|(EK)|(EU))[0-9]{9}GB/ig.test(trackingNumber)) &&
            !(/((C[A-Z])|(E[A-Z])|(B[A-Z])|(PA)|(N[D-S]))[0-9]{9}(?!GB)[A-Z]{2}/ig.test(trackingNumber))
          ) {

            return true;

          }

          return 'checkPFW';

        }

        return errorMap.INVALID_THIRTEEN;

      }

      // This is always a Parcelforce number.
      if (trackingNumberLength === 14) {

        return 'checkPFW';

      }

      return false;

    },

    errorMessageMap: (validationResult) => {

      let errResult;

      switch (validationResult) {

        case 'INVALID_EMPTY':

          errResult = configService.messages.validation.required;
          break;

        case 'INVALID_MIN_LENGTH':

          errResult = configService.messages.validation.minlength;
          break;

        case 'INVALID_MAX_LENGTH':

          errResult = configService.messages.validation.maxlength;
          break;

        case 'INVALID_CP':

          errResult = configService.messages.validation.cp;
          break;

        case 'TIME_OUT':

          errResult = configService.messages.system_error.tracking_unavailable;
          break;

        default:

          errResult = configService.messages.validation.invalid_entry;

          break;

      }

      return errResult;

    },

    splitNumber: (trackingNumber) => {

      const textGroup = [];
      let i;
      let len;
      let filteredtrackingNumber = '';

      filteredtrackingNumber = trackingNumber.replace(/\s/g, '');

      for (i = 0, len = filteredtrackingNumber.length; i < len; i += 4) {

        textGroup.push(filteredtrackingNumber.substr(i, 4));

      }

      return textGroup.join(' ');

    },

    isNumber: isNumber,

    isValueZero: isValueZero,

    areValidCoordinates: areValidCoordinates

  };

}

export default numberService;



// WEBPACK FOOTER //
// ./src/services/number.service.js